import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ChevronRight, ChevronLeft } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Shared";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export default function Onboarding() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [p, setP] = useState({
    sex: "male", birth_date: "1995-01-01", height_cm: 170, starting_weight: 70,
    activity_level: "moderate", goal: "general_health", experience: "beginner", units: "metric",
  });

  React.useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
    if (user && user.onboarded) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const upd = (k, v) => setP({ ...p, [k]: v });

  const finish = async () => {
    setLoading(true);
    try {
      await api.put("/profile", { name, profile: p, onboarded: true });
      await refresh();
      toast.success(t("welcomeTo"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const SelectField = ({ label, k, options }) => (
    <div>
      <Label>{label}</Label>
      <Select value={String(p[k])} onValueChange={(v) => upd(k, v)}>
        <SelectTrigger className="mt-1.5" data-testid={`onb-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{t(o)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const steps = [
    <div key="s1" className="space-y-4">
      <div>
        <Label>{t("name")}</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="onb-name" />
      </div>
      <SelectField label={t("sex")} k="sex" options={["male", "female"]} />
      <div>
        <Label>{t("birthDate")}</Label>
        <Input type="date" value={p.birth_date} onChange={(e) => upd("birth_date", e.target.value)} className="mt-1.5" data-testid="onb-birthdate" />
      </div>
    </div>,
    <div key="s2" className="space-y-4">
      <div>
        <Label>{t("height")} (cm)</Label>
        <Input type="number" value={p.height_cm} onChange={(e) => upd("height_cm", parseFloat(e.target.value) || 0)} className="mt-1.5 tabular-nums" data-testid="onb-height" />
      </div>
      <div>
        <Label>{t("startingWeight")} (kg)</Label>
        <Input type="number" step="0.1" value={p.starting_weight} onChange={(e) => upd("starting_weight", parseFloat(e.target.value) || 0)} className="mt-1.5 tabular-nums" data-testid="onb-weight" />
      </div>
      <SelectField label={t("units")} k="units" options={["metric", "imperial"]} />
    </div>,
    <div key="s3" className="space-y-4">
      <SelectField label={t("fitnessGoal")} k="goal" options={["lose_fat", "build_muscle", "recomp", "endurance", "general_health"]} />
      <SelectField label={t("activityLevel")} k="activity_level" options={["sedentary", "light", "moderate", "active", "very_active"]} />
      <SelectField label={t("experience")} k="experience" options={["beginner", "intermediate", "advanced"]} />
    </div>,
  ];

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo />
          <h1 className="mt-6 font-display text-2xl font-bold tracking-tight">{t("tellAbout")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("step")} {step + 1} / 3</p>
          <div className="mt-4 flex w-full gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>
        </div>
        <div className="rounded-2xl border bg-card p-6 slide-up">
          {steps[step]}
          <div className="mt-6 flex gap-3">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="gap-1" data-testid="onb-back">
                <ChevronLeft className="h-4 w-4" /> {t("back")}
              </Button>
            )}
            {step < 2 ? (
              <Button onClick={() => setStep(step + 1)} className="flex-1 gap-1" data-testid="onb-next">
                {t("next")} <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={finish} className="flex-1" disabled={loading} data-testid="onb-finish">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("completeSetup")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
